import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, X, Check, Clock, Trash2, Settings, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
 
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
 
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
 
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
 
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Default notifications
const defaultNotifications = [
  {
    id: 'welcome',
    title: 'Mirësevini në Xhami!',
    message: 'Aktivizoni njoftimet për kohët e namazit',
    time: new Date().toISOString(),
    read: false,
    type: 'info'
  }
];

const NotificationCenter = ({ isOpen = false, onClose = () => {}, deferredPrompt, setDeferredPrompt }) => {
  const [notifications, setNotifications] = useState([]);
  const [permission, setPermission] = useState('default');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [prayerAlerts, setPrayerAlerts] = useState({
    sabahu: true,
    dreka: true,
    ikindia: true,
    akshami: true,
    jacia: true
  });
  const [showSettings, setShowSettings] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  // Load saved settings
  useEffect(() => {
    const saved = localStorage.getItem('notificationSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      setSoundEnabled(settings.soundEnabled ?? true);
      setPrayerAlerts(settings.prayerAlerts ?? prayerAlerts);
    }
    
    // Load notifications
    const savedNotifs = localStorage.getItem('notifications');
    if (savedNotifs) {
      setNotifications(JSON.parse(savedNotifs));
    } else {
      setNotifications(defaultNotifications);
    }

    // Check notification permission
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }

    // Register Service Worker
    registerServiceWorker();
  }, []);

  // Save notifications
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Save settings
  useEffect(() => {
    localStorage.setItem('notificationSettings', JSON.stringify({
      soundEnabled,
      prayerAlerts
    }));
  }, [soundEnabled, prayerAlerts]);

  // Register Service Worker
  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('ServiceWorker registered:', registration);
        setSwRegistration(registration);
        
        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              addNotification({
                title: 'Përditësim i Ri',
                message: 'Aplikacioni u përditësua. Rifresko faqen për versionin e ri.',
                type: 'update'
              });
            }
          });
        });
      } catch (error) {
        console.error('ServiceWorker registration failed:', error);
      }
    }
  };

  // Request notification permission
  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Shfletuesi nuk mbështet njoftimet');
      return;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast.success('Njoftimet u aktivizuan!');
        addNotification({
          title: 'Njoftimet Aktive',
          message: 'Do të merrni njoftime për kohët e namazit',
          type: 'success'
        });
        
        // Show test notification
        showTestNotification();
        
        // Subscribe to Push Notifications (VAPID)
        subscribeToPushNotifications();
      } else {
        toast.error('Njoftimet u refuzuan');
      }
    } catch (error) {
      console.error('Permission request failed:', error);
    }
  };

  // Show test notification
  const showTestNotification = () => {
    if (permission !== 'granted') return;

    const options = {
      body: 'Njoftimet janë aktive! Do të merrni njoftime për namazin.',
      icon: '🕌',
      badge: '🕌',
      tag: 'test-notification',
      vibrate: [200, 100, 200],
      requireInteraction: false
    };

    if (swRegistration) {
      swRegistration.showNotification('Xhamia e Ali Demit', options);
    } else {
      new Notification('Xhamia e Ali Demit', options);
    }
  };

  // Subscribe to Push Notifications
  const subscribeToPushNotifications = async () => {
    if (!swRegistration || !VAPID_PUBLIC_KEY) return;

    try {
      const subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      await axios.post(`${API}/notifications/subscribe`, subscription);
      console.log('Push subscription sent to backend');
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
    }
  };

  // Add notification
  const addNotification = useCallback((notif) => {
    const newNotif = {
      id: Date.now().toString(),
      time: new Date().toISOString(),
      read: false,
      ...notif
    };
    
    setNotifications(prev => [newNotif, ...prev].slice(0, 50)); // Keep max 50
    
    // Play sound
    if (soundEnabled) {
      playNotificationSound();
    }
  }, [soundEnabled]);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant chime sound
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  // Mark notification as read
  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // Delete notification
  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Clear all notifications
  const clearAll = () => {
    setNotifications([]);
    toast.success('Të gjitha njoftimet u fshinë');
  };

  // Toggle prayer alert
  const togglePrayerAlert = (prayer) => {
    setPrayerAlerts(prev => ({
      ...prev,
      [prayer]: !prev[prayer]
    }));
  };

  // Get unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Format time
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Tani';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} orë`;
    return date.toLocaleDateString('sq-AL');
  };

  // Get notification icon color
  const getTypeColor = (type) => {
    switch (type) {
      case 'prayer': return 'bg-primary text-white';
      case 'success': return 'bg-green-500 text-white';
      case 'update': return 'bg-blue-500 text-white';
      case 'warning': return 'bg-amber-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop - click to close */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
              onClick={onClose}
              data-testid="notification-backdrop"
            />
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col z-[60]"
              data-testid="notification-panel"
            >
              {/* Header */}
              <div className="p-4 pt-14 md:pt-4 border-b bg-gradient-to-r from-primary to-primary-600 text-white min-h-[80px] md:min-h-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Bell className="w-5 h-5 flex-shrink-0" />
                    <h2 className="text-lg font-bold truncate">Njoftimet</h2>
                    {unreadCount > 0 && (
                      <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs flex-shrink-0">
                        {unreadCount} të reja
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setShowSettings(!showSettings)}
                      className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 active:bg-white/40"
                      data-testid="notification-settings"
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                    <button
                      onClick={onClose}
                      className="w-10 h-10 bg-red-500/80 rounded-full flex items-center justify-center hover:bg-red-600 active:bg-red-700"
                      data-testid="notification-close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Permission Banner */}
                {permission !== 'granted' && (
                  <motion.button
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={requestPermission}
                    className="w-full mt-3 p-3 bg-white/10 rounded-xl flex items-center gap-3 hover:bg-white/20 transition-colors"
                  >
                    <BellOff className="w-5 h-5 text-white/70" />
                    <div className="text-left flex-1">
                      <p className="font-semibold text-sm">Aktivizo Njoftimet</p>
                      <p className="text-xs text-white/70">Merr njoftime për namazin</p>
                    </div>
                  </motion.button>
                )}
              </div>

              {/* Settings Panel */}
              <AnimatePresence>
                {showSettings && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-gray-50 border-b overflow-hidden"
                  >
                    <div className="p-4 space-y-4">
                      {/* Sound Toggle */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {soundEnabled ? <Volume2 className="w-5 h-5 text-gray-500" /> : <VolumeX className="w-5 h-5 text-gray-400" />}
                          <span className="text-sm font-medium">Tingulli</span>
                        </div>
                        <button
                          onClick={() => setSoundEnabled(!soundEnabled)}
                          className={`w-11 h-6 rounded-full transition-colors ${soundEnabled ? 'bg-primary' : 'bg-gray-300'}`}
                        >
                          <motion.div
                            className="w-5 h-5 bg-white rounded-full shadow"
                            animate={{ x: soundEnabled ? 22 : 2 }}
                          />
                        </button>
                      </div>

                      {/* Prayer Alerts */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Njoftimet e Namazit</p>
                        <div className="grid grid-cols-5 gap-2">
                          {[
                            { key: 'sabahu', label: 'Sabahu' },
                            { key: 'dreka', label: 'Dreka' },
                            { key: 'ikindia', label: 'Ikindia' },
                            { key: 'akshami', label: 'Akshami' },
                            { key: 'jacia', label: 'Jacia' }
                          ].map((prayer) => (
                            <button
                              key={prayer.key}
                              onClick={() => togglePrayerAlert(prayer.key)}
                              className={`p-2 rounded-lg text-xs font-medium transition-all ${
                                prayerAlerts[prayer.key]
                                  ? 'bg-primary text-white'
                                  : 'bg-gray-200 text-gray-500'
                              }`}
                            >
                              {prayer.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              {notifications.length > 0 && (
                <div className="p-2 border-b flex gap-2">
                  <button
                    onClick={markAllAsRead}
                    className="flex-1 py-2 text-xs font-medium text-primary hover:bg-primary/5 rounded-lg flex items-center justify-center gap-1"
                  >
                    <Check className="w-3 h-3" />
                    Lexo të gjitha
                  </button>
                  <button
                    onClick={clearAll}
                    className="flex-1 py-2 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg flex items-center justify-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Fshi të gjitha
                  </button>
                </div>
              )}

              {/* Notifications List */}
              <div className="flex-1 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
                    <Bell className="w-12 h-12 mb-4 opacity-30" />
                    <p className="text-center">Nuk keni njoftime</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {notifications.map((notif) => (
                      <motion.div
                        key={notif.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`p-4 hover:bg-gray-50 transition-colors ${!notif.read ? 'bg-primary/5' : ''}`}
                        onClick={() => markAsRead(notif.id)}
                      >
                        <div className="flex gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getTypeColor(notif.type)}`}>
                            {notif.type === 'prayer' ? '🕌' : <Bell className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className={`font-semibold text-sm ${!notif.read ? 'text-gray-900' : 'text-gray-600'}`}>
                                {notif.title}
                              </h3>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(notif.id);
                                }}
                                className="text-gray-400 hover:text-red-500 p-1"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-500 line-clamp-2">{notif.message}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-400">{formatTime(notif.time)}</span>
                              {!notif.read && (
                                <span className="w-2 h-2 bg-primary rounded-full" />
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Install PWA Prompt */}
              {deferredPrompt && (
                <div className="p-4 border-t bg-gradient-to-r from-accent/10 to-primary/10">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📱</span>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-800">Instalo Aplikacionin</p>
                      <p className="text-xs text-gray-500 mb-2">Shto në ekranin kryesor për qasje më të shpejtë</p>
                      <button 
                        onClick={async () => {
                          if (!deferredPrompt) return;
                          deferredPrompt.prompt();
                          const { outcome } = await deferredPrompt.userChoice;
                          if (outcome === 'accepted') {
                            setDeferredPrompt(null);
                          }
                        }}
                        className="bg-primary text-white text-xs px-4 py-1.5 rounded-full font-medium shadow-sm hover:shadow active:scale-95 transition-all"
                      >
                        Instalo Tani
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NotificationCenter;
